import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { EMPTY_VALUE } from "app/pages/activation/components/HomeWithData/constants";
import { TopicTrafficChangeWidget } from "app/pages/activation/components/Shared/TopicChange/TopicChange";
import { useHomeContext } from "app/pages/activation/context/Home/HomeContext";
import { generateAutomationName } from "app/pages/activation/helpers/generateAutomationName";
import {
    ACTIVATION_PROJECT_ROUTE,
    DEFAULT_ACTIVATION_WEB_SOURCE,
    DEFAULT_ACTIVATION_GRANULARITY,
} from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getTrafficWidgetState } from "../../utils";
import { EmptyStateOverlayContent } from "../EmptyStateOverlayContent";
import { Loading } from "../MyTrafficShare/BodyByState/Loading";
import { TrafficWidgetLayout } from "../TrafficWidgetLayout";
import type { ICompetitorsTrafficShareProps } from "../types";

import Default from "./BodyByState/Default";
import Empty from "./BodyByState/Empty";
import * as SC from "./BodyByState/styles";
import { EMPTY_MESSAGE, EMPTY_TOPIC_NAME } from "./BodyByState/texts";
import { FOOTER_EMPTY_PROJECT, FOOTER_EMPTY_TOPIC, SHARED_TOPIC_CREATION_TOOLTIP, TITLE, TOOLTIP } from "./texts";

export const CompetitorsTrafficShare = ({
    trafficData,
    isLoading,
    isLocked,
    isEmpty,
    hasProjects,
    onCreateProject,
}: ICompetitorsTrafficShareProps) => {
    const { activeActivation } = useHomeContext();

    const isOwn = hasProjects ? activeActivation?.isOwn : true;
    const state = getTrafficWidgetState({ isEmpty, isLoading, isLocked });

    const trackingApi = activationTracking.homePageWithData;

    const onRedirectToTopics = async () => {
        if (!activeActivation?.id) {
            return;
        }

        await proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
            key: activeActivation?.id,
            country: activeActivation?.country,
            webSource: DEFAULT_ACTIVATION_WEB_SOURCE,
            timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
            shouldScrollToTopics: true,
        });
    };

    const onClick = async (event: React.MouseEvent) => {
        event.preventDefault();

        if (state === "default") {
            trackingApi.trackClickCompetitorsTrafficShare("view project");

            await onRedirectToTopics();

            return;
        }

        if (state === "empty") {
            if (activeActivation?.id) {
                await onRedirectToTopics();

                return;
            }

            if (!hasProjects) {
                trackingApi.trackClickCompetitorsTrafficShare("create a new project");
                onCreateProject();
                return;
            }

            trackingApi.trackClickCompetitorsTrafficShare("create topics");

            await onRedirectToTopics();
        }
    };

    return (
        <TrafficWidgetLayout
            titleKey={TITLE}
            onClick={activeActivation?.id ? onClick : undefined}
            tooltipKey={TOOLTIP}
            state={state}
            isLoading={isLoading}
            dataAutomation={generateAutomationName("home", ["share", "data", "competitors", "traffic", "share"])}
            bodyByState={{
                default: <Default trafficData={trafficData} />,
                empty: <Empty />,
                loading: <Loading isCompetitorsTrafficShare />,
            }}
            emptyStateOverlay={
                state === "empty" ? (
                    <EmptyStateOverlayContent
                        messageKey={EMPTY_MESSAGE}
                        ctaKey={hasProjects ? FOOTER_EMPTY_TOPIC : FOOTER_EMPTY_PROJECT}
                        onClick={onClick}
                        tooltipContent={!isOwn ? SHARED_TOPIC_CREATION_TOOLTIP : undefined}
                        isDisabled={!isOwn}
                    />
                ) : undefined
            }
            rightAsideByState={{
                default: (
                    <TopicTrafficChangeWidget
                        change={trafficData?.decreasedTopicShareOfVoiceChange}
                        topicName={trafficData?.decreasedTopicName}
                    />
                ),
                empty: (
                    <TopicTrafficChangeWidget
                        change={EMPTY_VALUE}
                        topicName={translate(EMPTY_TOPIC_NAME)}
                    />
                ),
                loading: (
                    <SC.RightAsideLoaderWrapper>
                        <PixelPlaceholderLoader
                            height="100%"
                            width="100%"
                        />
                    </SC.RightAsideLoaderWrapper>
                ),
            }}
        />
    );
};
